import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { useNotificationInbox } from '@/hooks/useNotificationInbox';
import { usePagination } from '@/hooks/usePagination';
import {
  formatNotificationTime,
  isNotificationUnread,
} from '@/utils/notificationUi';
import { showApiError } from '@/utils/apiError';

export function NotificationsCenterPage() {
  const pagination = usePagination(20);
  const { items, unread, loading, totalElements, totalPages, markRead, markAllRead } =
    useNotificationInbox({
      page: pagination.page,
      size: pagination.size,
      pollMs: 0,
    });

  const handleMarkRead = async (id: number) => {
    try {
      await markRead(id);
    } catch (err) {
      showApiError(err, 'Failed to mark notification read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
    } catch (err) {
      showApiError(err, 'Failed to mark all read');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Notifications"
        description={
          unread > 0
            ? `${unread} unread across all modules — email digests use your organization settings`
            : 'In-app alerts across all modules — email digests use your organization settings'
        }
        breadcrumbs={[{ label: 'My Work', to: '/work' }, { label: 'Notifications' }]}
        actions={
          <>
            {unread > 0 ? (
              <Button variant="secondary" onClick={() => void handleMarkAllRead()}>
                Mark all read ({unread})
              </Button>
            ) : null}
            <Link
              to="/work"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              My Work →
            </Link>
          </>
        }
      />

      <ul className="space-y-2">
        {loading && <li className="text-sm text-slate-500">Loading…</li>}
        {!loading && items.length === 0 && (
          <li className="card p-6 text-sm text-slate-500">No notifications yet.</li>
        )}
        {items.map((notification) => {
          const unreadItem = isNotificationUnread(notification);
          return (
            <li
              key={notification.id}
              className={`card p-4 text-sm ${unreadItem ? 'ring-2 ring-primary-500/20' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{notification.title}</p>
                    {unreadItem ? (
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:bg-primary-950 dark:text-primary-300">
                        New
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-slate-600 dark:text-slate-400">{notification.message}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-400">
                    <span>{formatNotificationTime(notification.createdAt)}</span>
                    {notification.notificationType ? (
                      <span className="uppercase">{notification.notificationType}</span>
                    ) : null}
                  </div>
                </div>
                {unreadItem ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void handleMarkRead(notification.id)}
                  >
                    Mark read
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      <TablePagination
        page={pagination.page}
        totalPages={totalPages}
        totalElements={totalElements}
        onPageChange={pagination.setPage}
        loading={loading}
        hasRows={items.length > 0}
      />
    </div>
  );
}
